import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ToolCallBlock } from "@/components/chat/ToolCallBlock";

/** Minimal tool-call shape — satisfied by both live ToolEvents and the
 *  persisted {name,args,result} records loaded from history. */
interface ToolCallLike {
  name: string;
  args: string;
  result?: string;
}

interface ToolCallsPanelProps {
  tools: ToolCallLike[];
  isStreaming: boolean;
  /** Initial expanded state (live = open, persisted history = collapsed). */
  defaultOpen?: boolean;
  /** When this flips truthy, auto-collapse once (the answer started). */
  collapseSignal?: boolean;
}

/**
 * Collapsible, fixed-height scroll container for the turn's tool calls.
 * Sits below the inner-thoughts widget and above the final answer, keeping a
 * long run of tool calls from flooding the chat. Modeled on ThinkingBlock.
 */
export function ToolCallsPanel({
  tools,
  isStreaming,
  defaultOpen = true,
  collapseSignal = false,
}: ToolCallsPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const prevCollapse = useRef(collapseSignal);
  useEffect(() => {
    if (collapseSignal && !prevCollapse.current) setOpen(false);
    prevCollapse.current = collapseSignal;
  }, [collapseSignal]);
  const hasPending = isStreaming && tools.some((t) => t.result === undefined);

  return (
    <div className="my-2 rounded-lg border border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-black/50 dark:text-white/50"
      >
        <Wrench className="h-3 w-3 shrink-0" />
        <span>Tools ({tools.length})</span>
        {hasPending && (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-coral" />
        )}
        {open ? (
          <ChevronDown className="ml-auto h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="ml-auto h-3 w-3 shrink-0" />
        )}
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto px-3 pb-2">
          {tools.map((tool, i) => (
            <ToolCallBlock
              key={i}
              name={tool.name}
              args={tool.args}
              result={tool.result}
            />
          ))}
        </div>
      )}
    </div>
  );
}
